import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Typography } from '@mui/joy';
import { getApiErrorMessage } from '../../lib/apiError';
import {
  useSetupProgress,
  useUpdateSetupProgress,
  useCompleteWizard,
} from '../../hooks/useSetupProgress';
import { SetupStepLayout } from './SetupStepLayout';
import { SetupCompleteModal } from './SetupCompleteModal';
import {
  OpportunityBoardsStep,
  JobBoardsStep,
  MarketingPhotosStep,
  KnowledgeGraphStep,
  ServicesStep,
  GBPConnectionStep,
  TeamInvitationsStep,
} from './steps';
import {
  SETUP_STEPS,
  getStepById,
  getNextStep,
  getPreviousStep,
  getStepIndex,
} from '../../types/setup';
import type { SetupStepId } from '../../types/setup';

export const QuickSetupWizard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: progress, isLoading, isError } = useSetupProgress();
  const updateProgress = useUpdateSetupProgress();
  const completeWizard = useCompleteWizard();

  const [currentStepId, setCurrentStepId] = useState<SetupStepId>('opportunity_boards');
  const [isStepLoading, setIsStepLoading] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);

  // The current step's save routine, handed over via useRegisterStepSave.
  // Steps that keep user input in local state register here so "Save &
  // Continue" persists it; steps that write on every action leave this null.
  const stepSave = useRef<(() => Promise<void>) | null>(null);

  const registerSave = useCallback((save: (() => Promise<void>) | null) => {
    stepSave.current = save;
  }, []);

  // Track if we've initialized the step
  const hasInitialized = useRef(false);

  // Initialize step from URL or progress - ONLY on initial load
  useEffect(() => {
    if (hasInitialized.current) return;
    if (!progress) return; // Wait for progress to load

    const stepParam = searchParams.get('step') as SetupStepId | null;

    if (stepParam && getStepById(stepParam)) {
      setCurrentStepId(stepParam);
    } else if (progress?.current_step) {
      setCurrentStepId(progress.current_step);
    }

    hasInitialized.current = true;
  }, [progress, searchParams]);

  // A save error belongs to the step that produced it.
  useEffect(() => {
    setStepError(null);
  }, [currentStepId]);

  // Update URL when step changes
  useEffect(() => {
    if (!hasInitialized.current) return; // Don't update URL until initialized

    const currentParams = new URLSearchParams(searchParams);
    currentParams.set('step', currentStepId);
    setSearchParams(currentParams, { replace: true });
  }, [currentStepId, setSearchParams]);

  const currentStep = getStepById(currentStepId);

  const handleStepComplete = useCallback(async () => {
    try {
      // Mark current step as complete
      await updateProgress.mutateAsync({
        step: currentStepId,
        completed: true,
      });

      // Move to next step or complete wizard
      const nextStep = getNextStep(currentStepId);

      if (nextStep) {
        setCurrentStepId(nextStep.id);
        await updateProgress.mutateAsync({
          step: currentStepId,
          completed: true,
          current_step: nextStep.id,
        });
      } else {
        // All steps done, complete the wizard
        await completeWizard.mutateAsync();
        setShowCompleteModal(true);
      }
    } catch (err) {
      console.error('Failed to complete step:', err);
      throw err;
    } finally {
      setIsStepLoading(false);
    }
  }, [currentStepId, updateProgress, completeWizard]);

  const handleNext = useCallback(async () => {
    setIsStepLoading(true);
    setStepError(null);
    try {
      // Persist the step's own data first. Marking progress complete without
      // this is what silently dropped everything the user typed.
      if (stepSave.current) {
        await stepSave.current();
      }
      await handleStepComplete();
    } catch (err) {
      // Do NOT advance — the step still holds the unsaved input, and moving on
      // would discard it exactly the way this button used to.
      console.error('Failed to save step:', err);
      setStepError(
        getApiErrorMessage(err, 'Could not save this step. Your changes are still here — please try again.'),
      );
      setIsStepLoading(false);
    }
  }, [handleStepComplete]);

  const handleBack = useCallback(async () => {
    const prevStep = getPreviousStep(currentStepId);
    if (prevStep) {
      setCurrentStepId(prevStep.id);
      await updateProgress.mutateAsync({
        step: currentStepId,
        current_step: prevStep.id,
      });
    }
  }, [currentStepId, updateProgress]);

  const handleSkip = useCallback(async () => {
    const nextStep = getNextStep(currentStepId);
    if (nextStep) {
      setCurrentStepId(nextStep.id);
      await updateProgress.mutateAsync({
        step: currentStepId,
        current_step: nextStep.id,
      });
    } else {
      // Last step, complete wizard
      await completeWizard.mutateAsync();
      setShowCompleteModal(true);
    }
  }, [currentStepId, updateProgress, completeWizard]);

  const handleStepClick = useCallback(
    async (stepId: SetupStepId) => {
      setCurrentStepId(stepId);
      await updateProgress.mutateAsync({
        step: currentStepId,
        current_step: stepId,
      });
    },
    [currentStepId, updateProgress]
  );

  const handleExit = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const handleCompleteModalClose = useCallback(() => {
    setShowCompleteModal(false);
    navigate('/dashboard');
  }, [navigate]);

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.level1',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (isError || !progress || !currentStep) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.level1',
        }}
      >
        <Typography level="h4" sx={{ mb: 2 }}>
          Something went wrong
        </Typography>
        <Typography level="body-md" sx={{ color: 'neutral.600', mb: 3 }}>
          Unable to load setup progress. Please try again.
        </Typography>
        <button onClick={() => window.location.reload()}>Refresh</button>
      </Box>
    );
  }

  // Render the appropriate step content.
  //
  // Steps holding user input in local state take `registerSave` so the footer
  // button can persist them. The rest write on every action and need nothing —
  // advancing is handled here either way.
  const renderStepContent = () => {
    switch (currentStepId) {
      case 'opportunity_boards':
        return <OpportunityBoardsStep />;
      case 'job_boards':
        return <JobBoardsStep />;
      case 'marketing_photos':
        return <MarketingPhotosStep registerSave={registerSave} />;
      case 'knowledge_graph':
        return <KnowledgeGraphStep registerSave={registerSave} />;
      case 'services':
        return <ServicesStep registerSave={registerSave} />;
      case 'gbp_connection':
        return <GBPConnectionStep />;
      case 'team_invitations':
        return <TeamInvitationsStep registerSave={registerSave} />;
      default:
        return null;
    }
  };

  // Determine next button label
  const getNextLabel = () => {
    const isLast = getStepIndex(currentStepId) === SETUP_STEPS.length - 1;

    switch (currentStepId) {
      case 'marketing_photos':
        return 'Upload & Continue';
      case 'gbp_connection':
        return progress[`${currentStepId}_completed` as keyof typeof progress]
          ? 'Continue'
          : 'Skip for Now';
      case 'team_invitations':
        return 'Send Invites & Finish';
      default:
        return isLast ? 'Finish Setup' : 'Save & Continue';
    }
  };

  // Determine if skip should be shown
  const shouldShowSkip = () => {
    // Show skip for all steps
    return true;
  };

  return (
    <>
      <SetupStepLayout
        step={currentStep}
        progress={progress}
        onNext={handleNext}
        onBack={handleBack}
        onSkip={handleSkip}
        onExit={handleExit}
        onStepClick={handleStepClick}
        isLoading={isStepLoading || updateProgress.isPending}
        nextLabel={getNextLabel()}
        showSkip={shouldShowSkip()}
      >
        {stepError && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {stepError}
          </Alert>
        )}
        {renderStepContent()}
      </SetupStepLayout>

      <SetupCompleteModal
        open={showCompleteModal}
        onClose={handleCompleteModalClose}
        onGoToDashboard={handleCompleteModalClose}
      />
    </>
  );
};

export default QuickSetupWizard;
